import React from 'react';
import {
  Badge, Box, HStack, Text, useColorModeValue,
} from '@chakra-ui/react';
import useCompanyRnwy from '../hooks/useCompanyRnwy';
import { RnwySubscriptionEnhancement, activeBySection, statusTag } from './forms/rnwyEnhancements';

// item 1546 — the BETA / COMING tags, shared by the in-app note and the public page. (1549: the
// EVERY LICENSE tag is gone — which group a row prints in says it.)
export const EnhancementTags: React.FC<{ e: RnwySubscriptionEnhancement }> = ({ e }) => (
  statusTag(e.status) ? (
    <Badge colorScheme={e.status === 'beta' ? 'orange' : 'gray'} fontSize="0.6rem" variant="subtle">
      {statusTag(e.status)}
    </Badge>
  ) : null
);

// item 1549 — one titled group of enhancement rows
export const EnhancementGroup: React.FC<{ title: string; rows: RnwySubscriptionEnhancement[]; titleColor?: string }> = ({ title, rows, titleColor }) => {
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  if (rows.length === 0) return null;
  return (
    <Box mb={2}>
      <Text fontSize="xs" fontWeight="bold" letterSpacing="0.04em" mb={1} color={titleColor}>{title}</Text>
      {rows.map((e) => (
        <Box key={e.id || e.name} mb={1}>
          <HStack spacing={2} wrap="wrap">
            <Text fontSize="sm" fontWeight="semibold">{e.name}</Text>
            <EnhancementTags e={e} />
          </HStack>
          {e.description && <Text fontSize="xs" color={sub}>{e.description}</Text>}
        </Box>
      ))}
    </Box>
  );
};

EnhancementGroup.defaultProps = { titleColor: undefined };

// item 1544 (Chad) → 1549 — the read-only license-features note, shown wherever an RNWY option is
// proposed, priced or enrolled (proposal form, Schedule B pricing preview, enroll panel). Two groups,
// straight from the two Settings → RNWY sections: (A) included with every license, (B) the
// subscription-only enhancements. Never priced. Renders nothing when both sections are empty.
const RnwySubscriptionEnhancementsNote: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { rnwy } = useCompanyRnwy();
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const border = useColorModeValue('gray.200', 'whiteAlpha.200');
  const { everyLicense, subscription } = activeBySection(rnwy?.subscriptionEnhancementsJson);
  if (everyLicense.length === 0 && subscription.length === 0) return null;
  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" p={compact ? 2 : 3} mt={2} mb={2}>
      <EnhancementGroup title="INCLUDED WITH EVERY LICENSE · Perpetual and subscription" rows={everyLicense} />
      <EnhancementGroup title="SUBSCRIPTION ENHANCEMENTS · subscription only (2-year / 5-year / SwitchPilot)" rows={subscription} />
      <Text fontSize="xs" color={sub}>Never priced. Edit in Settings › RNWY.</Text>
    </Box>
  );
};

RnwySubscriptionEnhancementsNote.defaultProps = { compact: false };

export default RnwySubscriptionEnhancementsNote;
